import { useCallback, useMemo, memo, useState, useEffect } from 'react'
import { getAmountOptions } from 'utils/loanSelector'
import Select, { ValueTransformer } from 'components/Input/Select'
import { Choice } from 'components/Input/ChoiceGroup'
import useStore from 'stores/utils/hooks/useStore'
import { observer } from 'mobx-react'
import { useText } from 'stores/utils/hooks/useText'
import SimpleSelect from 'components/Input/SimpleSelect'
import FieldDataCollector from '../../FieldDataCollector'
import { PathFormFieldProps } from '../../../interfaces/PathFormComponents.interface'
import WithFieldInteraction from '../../../utils/tracking/amplitude/WithFieldInteraction'
import { useFieldMetadata } from 'context/fieldMetadata'
import { usePatchedField } from '../../../stores/utils/hooks/usePatchedField'

interface OwnProps {
  defaultValue?: number
  clue?: string
}

export type PathAmountSelectProps = PathFormFieldProps<number> & OwnProps

const SelectInput = observer(
  ({
    path,
    clue,
    onTooltip,
    onChange,
    defaultValue
  }: PathAmountSelectProps) => {
    const { input, error } = usePatchedField(path, {
      defaultValue
    })
    const { traversal } = useStore()
    const {
      text: { lists }
    } = useText()

    const {
      removeErrorsForPath,
      handleMultiplePathFormFieldChanges,
      data: {
        common: { purpose }
      }
    } = traversal
    const { placeholder, tooltip, label = '' } = useFieldMetadata<number>(path)

    const isMortgageAllowed = (category: string) =>
      category === 'REAL_ESTATE_FINANCING' || category === 'RENOVATION'

    const [selectOptions, setAmountOptions] = useState<Array<Choice<number>>>(
      []
    )

    useEffect(() => {
      const amounts = getAmountOptions({
        amountOptions: lists?.loanSelections,
        loanCategory: purpose,
        isMortgageAllowed: isMortgageAllowed(purpose)
      })
      setAmountOptions(amounts)
    }, [lists, purpose])

    const changeHandler = useCallback(
      (value: number | undefined) => {
        /*
          when `common.amount` is sent alone, it can cause an error
          e.g.:
            general correct range for `common.amount` is different
            than for these ones with purposes REAL_ESTATE_FINANCING or RENOVATION.
            So patch with amount only (or without one of the two special purposes) AND > x will get failures returned.
        */
        if (path === 'common.amount') {
          handleMultiplePathFormFieldChanges([
            {
              path: 'common.amount',
              value
            },
            {
              path: 'common.purpose',
              value: traversal.data.common.purpose
            },
            {
              path: 'common.vehicle.price',
              value:
                (traversal.data.common.vehicle.initialPayment || 0) +
                (value || 0)
            }
          ])
        }

        input.onChange(value)
        input.onBlur()

        if (onChange) {
          onChange(value)
        }

        removeErrorsForPath(path)
      },
      [
        input,
        onChange,
        removeErrorsForPath,
        path,
        handleMultiplePathFormFieldChanges,
        traversal.data.common.purpose,
        traversal.data.common.vehicle.initialPayment
      ]
    )

    const blurHandler = useCallback(() => {
      input.onBlur()
    }, [input])
    const focusHandler = useCallback(() => {
      input.onFocus()
    }, [input])

    const valueTransformer: ValueTransformer<Choice<number>, number> = useMemo(
      () => ({
        selector: 'value',
        extractor: (choice) => choice.value
      }),
      []
    )

    if (path === 'common.amount') {
      return (
        <WithFieldInteraction
          trackFocusEvents={false}
          trackOnChange
          path={path}
        >
          <SimpleSelect
            options={selectOptions}
            clue={clue}
            error={error}
            label={label}
            placeholder={placeholder}
            name={input.name}
            value={`${input.value}`}
            onChange={changeHandler}
            onBlur={blurHandler}
            onFocus={focusHandler}
            valueTransformer={valueTransformer}
            optionLabel="label"
            smallTextField
            outerLabel
            centeredSelectedValue
          />
        </WithFieldInteraction>
      )
    }

    return (
      <WithFieldInteraction trackFocusEvents={false} trackOnChange path={path}>
        <Select<Choice<number>, number>
          options={selectOptions}
          clue={clue}
          error={error}
          label={label}
          tooltip={tooltip}
          placeholder={placeholder}
          name={input.name}
          value={`${input.value}`}
          onChange={changeHandler}
          onBlur={blurHandler}
          onFocus={focusHandler}
          onTooltip={onTooltip}
          valueTransformer={valueTransformer}
          optionLabel="label"
          outerLabel
          smallTextField
          showTooltipOnFieldClick
        />
      </WithFieldInteraction>
    )
  }
)

function PathSelect(props: PathAmountSelectProps) {
  const { path, labelPath, clue, onTooltip, onChange, defaultValue } = props

  return (
    <FieldDataCollector path={path} labelPath={labelPath}>
      <SelectInput
        path={path}
        clue={clue}
        onTooltip={onTooltip}
        onChange={onChange}
        defaultValue={defaultValue}
      />
    </FieldDataCollector>
  )
}

export default memo(PathSelect)
